// Pure audio model: sim state in, a mix description out. No Web Audio, no DOM, deterministic.
import type { BikeState } from '../sim/bike';
import type { Surface } from '../sim/terrain';
import type { BikeConfig } from '../sim/config';
import { clamp, dot, rotate, vec } from '../sim/math';

/** What the audio model needs from one frame of the sim. */
export interface AudioFrame {
  speed: number;          // m/s along the bike
  throttle: number;       // 0..1
  accel: number;          // m/s^2 along the bike
  rearSlip: number;       // telemetry.rearSlip (> 1 = wheelspin)
  lateralSpeed: number;   // m/s sideways (sliding)
  airborne: boolean;
  compressionVel: number; // m/s, max suspension compression rate (positive = compressing)
  surface: Surface;
}

export interface EngineConfig {
  idleRpm: number;
  launchRpm: number;      // clutch slips to hold about this in 1st at low speed
  shiftUpRpm: number;
  shiftDownRpm: number;
  limiterRpm: number;
  shiftCooldown: number;  // s
  ratios: number[];       // engine rpm per wheel rpm, per gear
  wheelRadius: number;    // m
}

// Loosely a 250 two-stroke: 5 gears, 9.8k limiter. 1st tops out around 12 m/s, 5th around 34 m/s.
export const DEFAULT_ENGINE: EngineConfig = {
  idleRpm: 1500,
  launchRpm: 6000,
  shiftUpRpm: 8800,
  shiftDownRpm: 4200,
  limiterRpm: 9800,
  shiftCooldown: 0.35,
  ratios: [27, 19.5, 15, 12.3, 10.4],
  wheelRadius: 0.35,
};

/** Audio engine model of a physics config (src/sim/bikes), so the heard gear and revs are the ridden bike's. */
export function engineConfigFor(c: Pick<BikeConfig, 'idleRpm' | 'launchRpm' | 'shiftUpRpm' | 'shiftDownRpm' | 'limiterRpm' | 'gearRatios' | 'wheelRadius'>): EngineConfig {
  return {
    ...DEFAULT_ENGINE,
    idleRpm: c.idleRpm, launchRpm: c.launchRpm, shiftUpRpm: c.shiftUpRpm, shiftDownRpm: c.shiftDownRpm, limiterRpm: c.limiterRpm,
    ratios: [...c.gearRatios], wheelRadius: c.wheelRadius,
  };
}

/** RPMs the engine loops were recorded at (procedural fallback uses the same grid). */
export const LOOP_RPMS: readonly number[] = [1500, 3500, 5500, 7500, 9500];
export const OFF_LOOP_RPM = 5000;

export interface EngineState { rpm: number; gear: number; load: number; shiftTimer: number }

export const createEngineState = (cfg: EngineConfig = DEFAULT_ENGINE): EngineState =>
  ({ rpm: cfg.idleRpm, gear: 1, load: 0, shiftTimer: 0 });

export const wheelToEngineRpm = (speed: number, gear: number, cfg: EngineConfig = DEFAULT_ENGINE): number =>
  (Math.abs(speed) / (2 * Math.PI * cfg.wheelRadius)) * 60 * cfg.ratios[gear - 1]!;

const approach = (from: number, to: number, rate: number, dt: number) => from + (to - from) * (1 - Math.exp(-rate * dt));

export function stepEngine(s: EngineState, f: AudioFrame, dt: number, cfg: EngineConfig = DEFAULT_ENGINE): void {
  s.shiftTimer = Math.max(0, s.shiftTimer - dt);
  const throttle = clamp(f.throttle, 0, 1);

  if (!f.airborne && s.shiftTimer <= 0) {
    const locked = wheelToEngineRpm(f.speed, s.gear, cfg);
    if (locked > cfg.shiftUpRpm && s.gear < cfg.ratios.length) { s.gear++; s.shiftTimer = cfg.shiftCooldown; }
    else if (locked < cfg.shiftDownRpm && s.gear > 1) { s.gear--; s.shiftTimer = cfg.shiftCooldown; }
  }

  let target: number, rise: number, fall: number;
  if (f.airborne) {
    target = cfg.idleRpm + throttle * (cfg.limiterRpm - cfg.idleRpm) * 1.05;
    rise = 6; fall = 2.5;
  } else {
    const locked = wheelToEngineRpm(f.speed, s.gear, cfg);
    target = locked;
    if (s.gear === 1 && locked < cfg.launchRpm) {
      target = Math.max(locked, cfg.idleRpm + throttle * (cfg.launchRpm - cfg.idleRpm)); // clutch slip
    }
    if (f.rearSlip > 1) target += (cfg.limiterRpm - target) * clamp(f.rearSlip - 1, 0, 1) * throttle * 0.8;
    target = Math.max(target, cfg.idleRpm);
    rise = 10; fall = 12;
  }
  s.rpm = approach(s.rpm, target, target > s.rpm ? rise : fall, dt);
  s.rpm = clamp(s.rpm, cfg.idleRpm * 0.8, cfg.limiterRpm);

  const loadTarget = throttle * (1 - 0.5 * clamp(f.accel / 10, 0, 1));
  s.load = clamp(approach(s.load, loadTarget, 12, dt), 0, 1);
}

/** Equal-power weights between the two loops that bracket rpm. Sum of squares is 1. */
export function loopWeights(rpm: number, loopRpms: readonly number[]): number[] {
  const w = loopRpms.map(() => 0);
  const n = loopRpms.length;
  if (rpm <= loopRpms[0]!) { w[0] = 1; return w; }
  if (rpm >= loopRpms[n - 1]!) { w[n - 1] = 1; return w; }
  let i = 0;
  while (rpm > loopRpms[i + 1]!) i++;
  const t = (rpm - loopRpms[i]!) / (loopRpms[i + 1]! - loopRpms[i]!);
  w[i] = Math.cos(t * Math.PI / 2);
  w[i + 1] = Math.sin(t * Math.PI / 2);
  return w;
}

export const playbackRate = (rpm: number, loopRpm: number): number => clamp(rpm / loopRpm, 0.5, 2);

export interface LayerMix { layers: { gain: number; rate: number }[]; offGain: number; offRate: number }

export function mixLayers(rpm: number, load: number, loopRpms: readonly number[] = LOOP_RPMS, offLoopRpm = OFF_LOOP_RPM,
  idleRpm = DEFAULT_ENGINE.idleRpm): LayerMix {
  const offGain = (1 - load) * clamp((rpm - idleRpm) / 2000, 0, 1);
  const onGain = 0.4 + 0.6 * load;
  const w = loopWeights(rpm, loopRpms);
  return {
    layers: w.map((g, i) => ({ gain: g * onGain, rate: playbackRate(rpm, loopRpms[i]!) })),
    offGain,
    offRate: playbackRate(rpm, offLoopRpm),
  };
}

const smoothstep = (x: number) => { const t = clamp(x, 0, 1); return t * t * (3 - 2 * t); };

/** Tyre scrub from wheelspin and sideways sliding. */
export const skidGain = (rearSlip: number, lateralSpeed: number): number =>
  smoothstep(Math.max(0, (rearSlip - 1) / 1.5) + Math.max(0, (Math.abs(lateralSpeed) - 1) / 4));

/** Rolling noise; the surface picks which sound, this picks how loud. */
export const rollGain = (speed: number, airborne: boolean): number =>
  airborne ? 0 : Math.pow(clamp(Math.abs(speed) / 20, 0, 1), 0.7);

export type RollFlavour = 'dirt' | 'sand' | 'grass' | 'mud' | 'water';
export const ROLL_FLAVOUR: Record<Surface, RollFlavour> = {
  clay: 'dirt', sandClay: 'dirt', roots: 'dirt', sand: 'sand', grass: 'grass', mud: 'mud', water: 'water',
};

export interface TriggerState { landingArmed: boolean; landingCd: number; splashCd: number }
export interface TriggerEvents { landing: number; splash: number } // strength 0..1, 0 = none

export const LANDING_THRESHOLD = 1.2; // m/s of compression
export const createTriggers = (): TriggerState => ({ landingArmed: true, landingCd: 0, splashCd: 0 });

export function stepTriggers(t: TriggerState, f: AudioFrame, dt: number): TriggerEvents {
  t.landingCd = Math.max(0, t.landingCd - dt);
  t.splashCd = Math.max(0, t.splashCd - dt);
  let landing = 0, splash = 0;
  if (f.compressionVel > LANDING_THRESHOLD) {
    if (t.landingArmed && t.landingCd <= 0) {
      landing = clamp((f.compressionVel - LANDING_THRESHOLD) / 4, 0.05, 1);
      t.landingCd = 0.3;
    }
    t.landingArmed = false;
  } else t.landingArmed = true;
  if ((f.surface === 'water' || f.surface === 'mud') && Math.abs(f.speed) > 1.5 && !f.airborne && t.splashCd <= 0) {
    splash = clamp(Math.abs(f.speed) / 12, 0.2, 1) * (f.surface === 'mud' ? 0.6 : 1);
    t.splashCd = 0.45;
  }
  return { landing, splash };
}

export interface FrameHistory { compression: number; speed: number }

/** Build an AudioFrame from sim state. Mutates prev to hold this frame's values. */
export function frameFromBike(b: BikeState, throttle: number, surface: Surface, prev: FrameHistory, dt: number): AudioFrame {
  const compression = Math.max(b.wheels[0].compression, b.wheels[1].compression);
  const speed = b.telemetry.speed;
  const f: AudioFrame = {
    speed,
    throttle,
    accel: dt > 0 ? (speed - prev.speed) / dt : 0,
    rearSlip: b.telemetry.rearSlip,
    lateralSpeed: dot(b.velocity, rotate(b.orientation, vec(1, 0, 0))),
    airborne: b.telemetry.airborne,
    compressionVel: dt > 0 ? (compression - prev.compression) / dt : 0,
    surface,
  };
  prev.compression = compression;
  prev.speed = speed;
  return f;
}
